import { AddOutlined, HourglassEmptyOutlined } from '@mui/icons-material';
import { Button, Divider, Stack, Tooltip, Typography } from '@mui/material';
import React from 'react';

import { CaseRetainerStepper } from './caseRetainerStepper';
import { CaseRetainersTable, type CaseRetainerAction } from './caseRetainersTable';
import { CaseSection } from './caseSection';
import { OfferSection } from './offerSection';
import Result from 'components/v2/common/Result';

import type { Application } from 'api';
import {
    getRetainerCreateState,
    getRetainerFlowSteps,
    isOfferLocked,
    sortRetainersOldestFirst,
} from 'helpers/applicationRetainers';
import { useApplicationMilestonesEditor } from 'hooks/useApplicationMilestonesEditor';
import useTranslate from 'hooks/useTranslate';
import type {
    ApplicationRetainer,
    ApplicationRetainersList,
    RetainerCreateReason,
    RetainerFlowStep,
} from 'types';

const CREATE_REASON_KEYS: Record<RetainerCreateReason, string> = {
    createOfferFirst: 'createOfferFirst',
    waitingForServiceAgreementSignature: 'waitingForServiceAgreementSignature',
    visaRetainerAutoGenerated: 'visaRetainerAutoGeneratedDescription',
    caseAlreadyHasRetainer: 'caseAlreadyHasRetainer',
    allRetainerTypesCreated: 'allRetainerTypesCreated',
    noAllowedRetainerTypes: 'noAllowedRetainerTypesDescription',
};

interface AgreementCasePanelProps {
    application: Application;
    list?: ApplicationRetainersList;
    pending: { agreementId: number; action: CaseRetainerAction } | null;
    onSaved: () => void;
    onCreateRetainer: () => void;
    onRetainerAction: (action: CaseRetainerAction, retainer: ApplicationRetainer) => void;
}

export const AgreementCasePanel: React.FC<AgreementCasePanelProps> = ({
    application,
    list,
    pending,
    onSaved,
    onCreateRetainer,
    onRetainerAction,
}) => {
    const { translate } = useTranslate();
    const offerLocked = isOfferLocked(list);
    const editor = useApplicationMilestonesEditor({ appId: String(application.id) });
    const steps: RetainerFlowStep[] = getRetainerFlowSteps({
        list,
        milestonesCount: editor.milestones.length,
    });
    const createState = getRetainerCreateState({
        list,
        milestonesCount: editor.milestones.length,
    });
    const caseRetainers = sortRetainersOldestFirst(list?.retainers ?? []);
    const createReason = createState.reason
        ? translate(CREATE_REASON_KEYS[createState.reason])
        : null;
    // TWO_STAGE: the server generates the visa retainer, so there is nothing to create
    const visaRetainerPending = createState.reason === 'visaRetainerAutoGenerated';

    const createButton = (
        <Button
            size="small"
            variant="contained"
            startIcon={<AddOutlined />}
            disabled={!createState.canCreate}
            onClick={onCreateRetainer}
            sx={{ textTransform: 'none' }}
        >
            {translate('createRetainer')}
        </Button>
    );

    return (
        <Stack spacing={2.5}>
            <CaseRetainerStepper steps={steps} />
            <CaseSection
                application={application}
                locked={offerLocked}
                onSaved={onSaved}
                extraActions={
                    visaRetainerPending ? null : createState.canCreate ? (
                        createButton
                    ) : (
                        <Tooltip title={createReason ?? ''}>
                            <span>{createButton}</span>
                        </Tooltip>
                    )
                }
            >
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                    {translate('retainers')}
                </Typography>
                <CaseRetainersTable
                    retainers={caseRetainers}
                    pending={pending}
                    handleAction={onRetainerAction}
                />
                {visaRetainerPending ? (
                    <Result
                        icon={<HourglassEmptyOutlined sx={{ fontSize: 32 }} />}
                        title={translate('visaRetainerAutoGenerated')}
                        description={createReason ?? ''}
                        sx={{ mt: 2, py: 2 }}
                    />
                ) : null}
            </CaseSection>
            <OfferSection locked={offerLocked} editor={editor} />
        </Stack>
    );
};
